import React, { useMemo } from 'react';
import { navBarPropTypes, navBarDefaultProps } from './navBar.propTypes';
import { Toolbar, useMediaQuery, useTheme, Container } from '@mui/material';
import { StyledNavBar } from './navBar.styles';
// import { categories as drawerCategories } from '../mobile_drawer/categories';
import Logo from '../logo/Logo';

import AccountButton from './AccountButton';
import MobileNav from './MobileNav';
import DesktopNav from './DesktopNav';
import NavBarSkeleton from './NavBarSkeleton';
import { useAuthContext } from './../../../context/AuthContext';
import { useMenuData } from './../../../hooks/useMenuData';

function NavBar({
  isAuthenticated,
  backgroundColor,
  position,
  elevation,
  actions,
}) {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('lm'));
  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [hoveredButton, setHoveredButton] = React.useState(null);
  const [menuCloseTimeout, setMenuCloseTimeout] = React.useState(null);
  const [menuLeaveTimeout, setMenuLeaveTimeout] = React.useState(null);
  const isButtonHoveredRef = React.useRef(false);
  const { isAuthorized } = useAuthContext();

  // Get and format menu data reactively - components update when data arrives
  const guestMenuData = useMenuData('guest_navigation_menu');
  const customerMenuData = useMenuData('customer_navigation_menu');

  // Check if menu data is loaded (has actual menu items, not just empty state)
  const isMenuLoaded = useMemo(() => {
    const currentMenu = isAuthorized ? customerMenuData : guestMenuData;
    return currentMenu.desktopNavMenu && currentMenu.desktopNavMenu.length > 0;
  }, [isAuthorized, guestMenuData, customerMenuData]);

  const pages = useMemo(
    () => (isAuthorized ? customerMenuData.desktopNavMenu : guestMenuData.desktopNavMenu),
    [isAuthorized, customerMenuData, guestMenuData],
  );

  const drawerCategories = useMemo(
    () => (isAuthorized ? customerMenuData.drawerCategories : guestMenuData.drawerCategories),
    [isAuthorized, customerMenuData, guestMenuData],
  );

  const toggleDrawer = open => () => {
    setDrawerOpen(open);
  };
  const handleMenuOpen = (event, buttonType) => {
    
    // eslint-disable-next-line no-use-before-define
    handleMenuClose();
    clearTimeout(menuCloseTimeout);
    clearTimeout(menuLeaveTimeout);
    setHoveredButton(buttonType);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setHoveredButton(null);
  };

  const handleMenuLeave = () => {
    if (!isButtonHoveredRef.current) {
      const timeout = setTimeout(() => {
        handleMenuClose();
      }, 500);
      setMenuCloseTimeout(timeout);
    }
  };

  const handleMenuLeaveMenu = () => {
    if (!isButtonHoveredRef.current) {
      clearTimeout(menuCloseTimeout);
      const timeout = setTimeout(() => {
        handleMenuClose();
      }, 150);
      setMenuLeaveTimeout(timeout);
    }
  };

  const handleMenuEnter = () => {
    clearTimeout(menuCloseTimeout);
    clearTimeout(menuLeaveTimeout);
  };

  const handleButtonEnter = (event, buttonType) => {
    
    clearTimeout(menuCloseTimeout);
    clearTimeout(menuLeaveTimeout);
    isButtonHoveredRef.current = true;
    handleMenuOpen(event, buttonType);
  };

  const handleButtonLeave = () => {
    isButtonHoveredRef.current = false;
    handleMenuLeave();
  };

  
  const getButtonMenuItems = buttonType => pages.find(e => e.title === buttonType)?.menuItems || [];
  const getMenuColumns = buttonType => pages.find(e => e.title === buttonType)?.menuItems.length || 1;
  const accountButtons = pages[pages.length-1]; // To handle Log in for guest menu and Logout for customer menu

  // Always show branding (Logo + MobileNav); skeleton only the menu-dependent nav links and account area
  return (
    <StyledNavBar
      position={position}
      backgroundColor={backgroundColor}
      elevation={elevation}
      theme={theme}
      sx={{ zIndex: { xs: 800, lg: 1500 } }}
      aria-label="NavBar"
    >
      <Container
        maxWidth="100%"
        sx={{ px: { xs: 0, lg: '40px' }, mx: { xs: 0 } }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: '78px', lg: '92px' },
          }}
        >
          <Logo
            alt="California Casualty Header Logo"
            logoheight="55.1px"
            logowidth="267.44px"
            sx={{ display: { xs: 'none', lg: 'flex' }, mx: '10px' }}
            actions={actions}
          />
          <MobileNav
            isSmallMobile={isSmallMobile}
            isDrawerOpen={isDrawerOpen}
            toggleDrawer={toggleDrawer}
            drawerCategories={drawerCategories}
            actions={actions}
          />
          {isMenuLoaded ? (
            <>
              <DesktopNav
                pages={pages}
                hoveredButton={hoveredButton}
                handleButtonEnter={handleButtonEnter}
                handleButtonLeave={handleButtonLeave}
                getButtonMenuItems={getButtonMenuItems}
                getMenuColumns={getMenuColumns}
                menuAnchorEl={menuAnchorEl}
                handleMenuClose={handleMenuClose}
                handleMenuLeaveMenu={handleMenuLeaveMenu}
                handleMenuEnter={handleMenuEnter}
              />
              <AccountButton
                isAuthenticated={isAuthorized}
                isSmallMobile={isSmallMobile}
                item={accountButtons}
              />
            </>
          ) : (
            <NavBarSkeleton contentOnly />
          )}
        </Toolbar>
      </Container>
    </StyledNavBar>
  );
}

NavBar.propTypes = navBarPropTypes;
NavBar.defaultProps = navBarDefaultProps;

export default React.memo(NavBar);
